import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Lock, Check } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

function arraysEqual(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
}

// Fisher–Yates shuffle (returns new array of indices 0..n-1)
function shuffleIndices(n) {
    const a = Array.from({ length: n }, (_, i) => i);
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

// Build display order (option strings) from saved order (indices) or legacy ranking (strings)
function getRankingFromAnswer(userAnswer, options) {
    if (!userAnswer || !options?.length) return null;
    if (Array.isArray(userAnswer.order) && userAnswer.order.length) {
        const mapped = userAnswer.order.map((i) => options[i]).filter(Boolean);
        if (mapped.length) return mapped;
    }
    if (Array.isArray(userAnswer.ranking) && userAnswer.ranking.length) {
        return [...userAnswer.ranking];
    }
    return null;
}

const RankingForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const options = content.options || [];
    const randomize = content.settings?.randomize === true;
    const restrictedRanking = content.settings?.restrictedRanking === true;
    const requiredOptions = Math.min(
        options.length,
        Math.max(1, Number(content.settings?.requiredOptions) || options.length)
    );

    const displayOrder = useMemo(
        () => (randomize && options.length ? shuffleIndices(options.length) : Array.from({ length: options.length }, (_, i) => i)),
        [options.length, randomize]
    );

    const displayOptions = useMemo(() => displayOrder.map((i) => options[i]), [options, displayOrder]);

    const [ranking, setRanking] = useState(() => {
        const fromAnswer = getRankingFromAnswer(userAnswer, options);
        if (fromAnswer) return fromAnswer;
        return displayOptions.length ? [...displayOptions] : [...options];
    });

    // Guards against a socket-reconnect resync (JoinPage re-fetches the active activity and
    // calls setUserAnswer with a freshly-parsed object on every reconnect, even when nothing
    // actually changed) silently clobbering a reorder the participant is mid-way through but
    // hasn't submitted yet. Flips true the moment a drag/move happens since the last submit,
    // and resets on a successful submit or when userAnswer goes back to null (a genuinely new
    // activity, where there's nothing local worth preserving).
    const hasLocalEditsRef = useRef(false);

    useEffect(() => {
        if (userAnswer == null) {
            hasLocalEditsRef.current = false;
        }
        if (hasLocalEditsRef.current) return;
        const fromAnswer = getRankingFromAnswer(userAnswer, options);
        if (fromAnswer) {
            setRanking(fromAnswer);
        } else if (options.length && displayOrder.length === options.length) {
            setRanking(displayOrder.map((i) => options[i]));
        }
    }, [userAnswer, options, displayOrder]);

    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleDragStart = (e, index) => {
        if (optionsDisabled) return;
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragIndex !== null && dragIndex !== index) setDragOverIndex(index);
    };

    const handleDragLeave = () => setDragOverIndex(null);

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        setDragOverIndex(null);
        if (dragIndex == null || optionsDisabled || dragIndex === dropIndex) {
            setDragIndex(null);
            return;
        }
        const next = [...ranking];
        const [removed] = next.splice(dragIndex, 1);
        next.splice(dropIndex, 0, removed);
        hasLocalEditsRef.current = true;
        setRanking(next);
        setDragIndex(null);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    // Touch/keyboard fallback: native HTML5 drag-and-drop never fires on mobile
    // touch devices, so moving an item up/down via tap or Enter/Space swaps it
    // with its neighbor in the same `ranking` array the drag handlers mutate.
    const moveItem = (index, direction) => {
        if (optionsDisabled) return;
        const target = index + direction;
        if (target < 0 || target >= ranking.length) return;
        const next = [...ranking];
        [next[index], next[target]] = [next[target], next[index]];
        hasLocalEditsRef.current = true;
        setRanking(next);
    };

    const fullOrder = ranking.map((opt) => options.indexOf(opt)).filter((i) => i >= 0);
    const currentOrder =
        restrictedRanking && requiredOptions < fullOrder.length
            ? fullOrder.slice(0, requiredOptions)
            : fullOrder;
    const submittedOrder = userAnswer?.order ?? userAnswer?.ranking?.map((opt) => options.indexOf(opt)) ?? [];
    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && arraysEqual(currentOrder, Array.isArray(submittedOrder) ? submittedOrder : []);
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : 'Submit ranking')
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response';
    const submitDisabled = isPreview || isLocked || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Rank the items'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}
            {content.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question visual" />
            )}
            <div className={styles.rankingInstructions} role="status" aria-live="polite">
                <strong>How to rank:</strong> Drag each row up or down to set your order. The item at <strong>#1</strong> is your top choice. When done, tap <strong>Submit ranking</strong>.
                {restrictedRanking && requiredOptions < options.length && (
                    <span> Only your top <strong>{requiredOptions}</strong> choices will count.</span>
                )}
            </div>
            <span className={styles.hint}>
                {restrictedRanking && requiredOptions < options.length ? (
                    <>Drag items to reorder — <strong>#1 is your top choice</strong>. Only your top <strong>{requiredOptions}</strong> will count. Then submit.</>
                ) : (
                    <>Drag items to reorder — <strong>#1 is your top choice</strong>. Then submit.</>
                )}
            </span>
            <div className={styles.correctOrderList} role="list">
                {ranking.map((opt, i) => (
                    <div
                        key={i}
                        role="listitem"
                        aria-label={opt ? `${i + 1}. ${opt}` : `Option ${i + 1}`}
                        aria-disabled={optionsDisabled}
                        className={`${styles.correctOrderItem} ${dragIndex === i ? styles.correctOrderItemDragging : ''} ${dragOverIndex === i ? styles.correctOrderItemDragOver : ''}`}
                        draggable={!optionsDisabled}
                        onDragStart={(e) => handleDragStart(e, i)}
                        onDragOver={(e) => handleDragOver(e, i)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, i)}
                        onDragEnd={handleDragEnd}
                    >
                        <span className={styles.correctOrderGrip} title="Drag to reorder" aria-label="Drag to reorder">
                            <ArrowUpDown size={18} />
                        </span>
                        <span className={styles.correctOrderMoveGroup}>
                            <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => moveItem(i, -1)}
                                disabled={optionsDisabled || i === 0}
                                aria-label="Move up"
                            >
                                <ArrowUp size={16} />
                            </button>
                            <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => moveItem(i, 1)}
                                disabled={optionsDisabled || i === ranking.length - 1}
                                aria-label="Move down"
                            >
                                <ArrowDown size={16} />
                            </button>
                        </span>
                        <span className={styles.correctOrderNumber}>{i + 1}</span>
                        <span className={styles.correctOrderLabel}>{opt || `Option ${i + 1}`}</span>
                    </div>
                ))}
            </div>
            <button
                className={styles.submitBtn}
                onClick={() => {
                    if (optionsDisabled) return;
                    onSubmit({ order: currentOrder.slice() });
                    hasLocalEditsRef.current = false;
                }}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
        </div>
    );
};

export default RankingForm;
